import { CaseCalculationAlternativePart, CaseCalculationPart } from '../../../interfaces/Case';
import { Organization } from '../../../interfaces/Organization';

export const NOT_SELECTED = 9999999999999;

export const makeSupplier = (id: number, name: string): Organization =>
  ({
    id,
    name,
    displayName: name,
    fullName: name,
    companyNumber: String(id),
    isActive: true
  } as Organization);

const clampQty = (n: number): number => Math.max(1, Math.floor(n) || 1);

export const emptyAlternativeDraft = (): CaseCalculationAlternativePart => ({
  id: 0,
  caseCalculationHistoryId: 0,
  name: '',
  alternativeGuideNumber: '',
  alternativePartCode: '',
  alternativePrice: null,
  alternativePartDiscount: null,
  alternativePartQualityId: 0,
  alternativePartQuality: null,
  deliveryTime: null,
  validityTime: null,
  codeType: null,
  orderCode: null,
  type: null,
  locationId: null,
  excludeFromOrder: null,
  producer: '',
  supplier: null,
  supplierId: null,
  originalPartId: 0,
  selectedAlternativePart: NOT_SELECTED,
  availableQuantity: null,
  isInStock: false,
  stockLocation: '',
  remarks: '',
  minQty: 0,
  packing: null,
  bundleItem: null,
  bundleNo: null,
  bundleQuantity: null,
  partFromGroup: NOT_SELECTED,
  additionalInfo: null,
  quantityAdditionalInfo: null,
  isChild: false,
  productId: null,
  codEan: null,
  expectedDeliveryDate: null,
  groupedUniqueId: null,
  alternativeNetPrice: null,
  alternativePriceWithoutDiscount: null,
  additionInsurer: null,
  alternativePartQualityName: null,
  supplierName: null,
  partStockId: null,
  isOwnStock: false,
  isSelectedAlternativePart: false,
  isVariantSelected: false,
  isServiceRecommendation: false,
  isClientSelection: false
});

export const makeAlternativePart = (
  id: number,
  originalPartId: number,
  draft: CaseCalculationAlternativePart
): CaseCalculationAlternativePart => {
  const merged = { ...emptyAlternativeDraft(), ...draft };
  const quality = merged.alternativePartQuality;
  const guide = merged.alternativeGuideNumber.trim();
  const availableQuantity =
    merged.availableQuantity == null ? null : Math.max(0, merged.availableQuantity);

  const supplierName = merged.supplier ? merged.supplier.name : merged.supplierName;
  const supplier = merged.supplier ?? (supplierName ? makeSupplier(id, supplierName) : null);
  return {
    ...merged,
    id,
    originalPartId,
    name: merged.name.trim(),
    alternativeGuideNumber: guide,
    alternativePartCode: guide,
    producer: merged.producer.trim(),
    stockLocation: merged.stockLocation.trim(),
    remarks: merged.remarks.trim(),
    alternativePartQualityId: quality ? quality.id : 0,
    supplier,
    supplierId: supplier ? supplier.id : null,
    supplierName: supplierName ?? null,
    availableQuantity,
    isInStock: availableQuantity != null && availableQuantity > 0,
    selectedAlternativePart: NOT_SELECTED,
    partFromGroup: NOT_SELECTED,
    alternativeNetPrice: Math.round(merged.alternativePrice * 0.9 * 100) / 100
  };
};

export const emptyCalculationPartDraft = (): CaseCalculationPart => ({
  id: 0,
  caseCalculationHistoryId: 0,
  calculationSectionId: 1,
  calculationSection: null,
  name: '',
  pieceNr: 1,
  guideNumber: '',
  originalGuideNumber: '',
  carPartCategoryId: null,
  carPartCategory: null,
  originalPrice: 0,
  supplier: null,
  supplierId: null,
  deliveryTime: null,
  validityTime: null,
  codeType: null,
  excludeFromOrder: null,
  orderCode: null,
  type: null,
  locationId: null,
  producer: null,
  alternativeGuideNumber: null,
  alternativePartCode: null,
  alternativePartQualityId: null,
  alternativePartQuality: null,
  alternativePrice: null,
  alternativePartStockQuantity: null,
  alternativePartStockLocation: null,
  alternativePartName: null,
  alternativePartDiscount: null,
  discount: 0,
  remarks: null,
  alternativePartRemarks: null,
  amount: 0,
  solutionValueId: null,
  solutionValue: null,
  solutionComment: '',
  repairMethod: '',
  minQty: 0,
  packing: null,
  bundleItem: null,
  bundleNo: null,
  bundleQuantity: null,
  package: null,
  isOrdered: false,
  isChild: false,
  productId: null,
  codEan: null,
  groupedUniqueId: null,
  additionValue: 0,
  additionFixedValue: 0,
  alternativeNetPrice: null,
  alternativePriceWithoutDiscount: null,
  additionInsurer: null,
  totalAmount: 0,
  isOwnStock: false,
  auxiliarInformation: null,
  editOriginalPrice: false,
  isNew: true,
  alternativePartDescription: null,
  availableQuantity: null,
  isInStock: false,
  supportComment: null,
  partStockId: null,
  expectedDeliveryDate: null,
  caseCalculationPartInterogations: [],
  manualGTModified: false,
  serviceCalculationPartId: null,
  suggestedVariantsJson: null,
  isServiceRecommendationChanged: false,
  isLoading: false,
  hasBeenInterogated: false,
  alternativeParts: [],
  isExpanded: true
});

export const makeCalculationPart = (
  id: number,
  draft: CaseCalculationPart
): CaseCalculationPart => {
  const merged = { ...emptyCalculationPartDraft(), ...draft };
  const guide = merged.guideNumber.trim();
  const quality = merged.carPartCategory;
  return {
    ...merged,
    id,
    name: merged.name.trim(),
    guideNumber: guide,
    originalGuideNumber: guide,
    pieceNr: clampQty(merged.pieceNr),
    carPartCategoryId: quality ? quality.id : null,
    isNew: true,
    isLoading: false,
    hasBeenInterogated: false,
    isExpanded: true
  };
};
